import { useMemo, useState, type ReactNode } from 'react'
import { useStore } from '@/store'
import type { ViewName } from '@/types'
import { useAuth } from '@/auth/hooks'
import { authClient } from '@/auth/authClient'
import { useAuthStore } from '@/auth/authStore'
import { useAnalysisDispatch } from '@/analysis/hooks'
import { useCatalogStore, type CatalogStreamItem } from '@/store/catalogStore'
import { filteredOpenIncidentCount } from '@/lib/filteredIncidents'

type NavItemDef = { view: ViewName; label: string; icon: string; permission?: string }

const MANAGEMENT_ITEMS: NavItemDef[] = [
  { view: 'incidents', label: 'Incidents', icon: 'alert' },
  { view: 'streams', label: 'Streams', icon: 'stream', permission: 'stream:read' },
  { view: 'reports', label: 'Reports', icon: 'doc', permission: 'user:manage' },
  { view: 'users', label: 'Users', icon: 'users', permission: 'user:manage' },
  { view: 'settings', label: 'Settings', icon: 'cog' },
]

const ANALYTICS_LABELS: Record<string, string> = {
  world: 'World view',
  hex: 'Hex grid',
  deep: 'Deep dive',
}

const ICONS: Record<string, React.ReactNode> = {
  globe:  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="6"/><path d="M2 8h12M8 2a10 10 0 010 12"/></svg>,
  alert:  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 2l6 12H2z"/><line x1="8" y1="7" x2="8" y2="10"/><circle cx="8" cy="12.5" r=".5" fill="currentColor"/></svg>,
  doc:    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="2" width="10" height="12" rx="1"/><line x1="5" y1="6" x2="11" y2="6"/><line x1="5" y1="9" x2="8.5" y2="9"/></svg>,
  users:  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="7" cy="5" r="3"/><path d="M1 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/></svg>,
  cog:    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="2.5"/><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.2 3.2l1.4 1.4M11.4 11.4l1.4 1.4M3.2 12.8l1.4-1.4M11.4 4.6l1.4-1.4"/></svg>,
  server: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="2.5" width="12" height="4.5" rx="1"/><rect x="2" y="9" width="12" height="4.5" rx="1"/><circle cx="4.5" cy="4.75" r=".6" fill="currentColor"/><circle cx="4.5" cy="11.25" r=".6" fill="currentColor"/></svg>,
  stream: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 8c2-3 4-3 6 0s4 3 6 0"/><path d="M3.5 11c1.5-2 2.5-2 4.5 0s3 2 4.5 0"/><circle cx="8" cy="5" r="1.2" fill="currentColor"/></svg>,
}

type CatalogItem = { code: string; name: string; stream_count?: number }

function initials(displayName: string | undefined, email: string | undefined): string {
  const source = (displayName ?? email ?? '?').trim()
  const parts = source.split(/[\s@.]+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase()
}

function primaryRoleLabel(permissions: string[] | undefined): string {
  if (!permissions || permissions.length === 0) return 'Member'
  if (permissions.includes('user:manage')) return 'Admin'
  if (permissions.includes('stream:write')) return 'Editor'
  return 'Viewer'
}

function toCountMap(items: CatalogItem[]): Map<string, number> {
  const m = new Map<string, number>()
  for (const it of items) m.set(it.code, Number(it.stream_count) || 0)
  return m
}

/** Active = telemetry status online (emitting within the live grace window). */
function activeCountMaps(streams: CatalogStreamItem[]): {
  game: Map<string, number>
  studio: Map<string, number>
  brand: Map<string, number>
} {
  const game = new Map<string, number>()
  const studio = new Map<string, number>()
  const brand = new Map<string, number>()
  const bump = (m: Map<string, number>, code: string) => {
    const key = String(code ?? '').trim()
    if (!key) return
    m.set(key, (m.get(key) ?? 0) + 1)
  }
  for (const s of streams) {
    if (s.deleted_at) continue
    if (s.status !== 'online') continue
    bump(game, s.game_code)
    bump(studio, s.studio_code)
    for (const b of s.brand_ids ?? []) bump(brand, b)
  }
  return { game, studio, brand }
}

function formatCatalogCount(active: number, inventory: number): string {
  if (active > 0) return `(${active}/${inventory})`
  return `(${inventory})`
}

function sortCatalogItems(
  items: CatalogItem[],
  activeCounts: Map<string, number>,
): CatalogItem[] {
  return [...items].sort((a, b) => {
    const aa = activeCounts.get(a.code) ?? 0
    const ba = activeCounts.get(b.code) ?? 0
    const aLive = aa > 0 ? 1 : 0
    const bLive = ba > 0 ? 1 : 0
    if (bLive !== aLive) return bLive - aLive
    if (ba !== aa) return ba - aa
    return a.code.localeCompare(b.code)
  })
}

export default function Sidebar() {
  const view = useStore((s) => s.view)
  const incidents = useStore((s) => s.incidents)
  const filters = useStore((s) => s.filters)
  const setView = useStore((s) => s.setView)
  const clearDrill = useStore((s) => s.clearDrill)
  const descendToStreams = useStore((s) => s.descendToStreams)
  const setFilterOnly = useStore((s) => s.setFilterOnly)
  const setDimension = useStore((s) => s.setDimension)

  const openCount = useMemo(
    () => filteredOpenIncidentCount(incidents, filters),
    [incidents, filters],
  )

  const gameFilter = filters.game
  const studioFilter = filters.studio
  const brandFilter = filters.brand

  const { user } = useAuth()
  const permissions = user?.permissions ?? []
  const dispatch = useAnalysisDispatch()

  const games = useCatalogStore((s) => s.games)
  const studios = useCatalogStore((s) => s.studios)
  const brands = useCatalogStore((s) => s.brands)
  const streams = useCatalogStore((s) => s.streams)

  const gameCounts = useMemo(() => toCountMap(games), [games])
  const studioCounts = useMemo(() => toCountMap(studios), [studios])
  const brandCounts = useMemo(() => toCountMap(brands), [brands])
  const activeMaps = useMemo(() => activeCountMaps(streams), [streams])

  const gamesSorted = useMemo(() => sortCatalogItems(games, activeMaps.game), [games, activeMaps.game])
  const studiosSorted = useMemo(() => sortCatalogItems(studios, activeMaps.studio), [studios, activeMaps.studio])
  const brandsSorted = useMemo(() => sortCatalogItems(brands, activeMaps.brand), [brands, activeMaps.brand])

  const managementItems = MANAGEMENT_ITEMS.filter(
    (item) => !item.permission || permissions.includes(item.permission),
  )
  const analyticsLabel = ANALYTICS_LABELS[view] ?? 'World view'
  const analyticsActive = view === 'world' || view === 'hex' || view === 'deep'

  function onCatalogSelect(axis: 'game' | 'studio' | 'brand', code: string | null) {
    if (!code) {
      setFilterOnly(axis, null)
      return
    }
    descendToStreams(axis, code)
    setDimension('stream')
    setView('hex')
  }

  async function signOut() {
    try { await authClient.logout() } catch { /* ignore */ }
    dispatch({ type: 'ws/disconnect' })
    useAuthStore.getState().setAnonymous(null)
  }

  return (
    <aside style={{
      width: 'var(--sidebar-w)', flexShrink: 0,
      background: 'var(--bg-1)', borderRight: '0.5px solid var(--bd-0)',
      display: 'flex', flexDirection: 'column',
      height: '100%',
      backdropFilter: 'blur(16px)',
    }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        <div
          onClick={() => { clearDrill(); setView('world') }}
          className={analyticsActive ? 'nav-item nav-item-active' : 'nav-item'}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 10px', margin: '12px 8px 4px', borderRadius: 'var(--radius)',
            color: analyticsActive ? 'var(--t-0)' : 'var(--t-1)',
            background: analyticsActive ? 'var(--bg-3)' : 'transparent',
            fontWeight: analyticsActive ? 500 : 400,
            cursor: 'pointer', fontSize: 13,
          }}
          title="Return to World view"
        >
          <span style={{ width: 13, height: 13, flexShrink: 0, opacity: analyticsActive ? 0.85 : 0.45, display: 'flex' }}>
            {ICONS.globe}
          </span>
          {analyticsLabel}
        </div>

        <CatalogFilterSection
          title="Games"
          items={gamesSorted}
          counts={gameCounts}
          activeCounts={activeMaps.game}
          selected={gameFilter[0] ?? null}
          onSelect={(code) => onCatalogSelect('game', code)}
        />
        <CatalogFilterSection
          title="Studios"
          items={studiosSorted}
          counts={studioCounts}
          activeCounts={activeMaps.studio}
          selected={studioFilter[0] ?? null}
          onSelect={(code) => onCatalogSelect('studio', code)}
        />
        <CatalogFilterSection
          title="Brands"
          items={brandsSorted}
          counts={brandCounts}
          activeCounts={activeMaps.brand}
          selected={brandFilter[0] ?? null}
          onSelect={(code) => onCatalogSelect('brand', code)}
        />

        {managementItems.length > 0 && (
          <>
            <div style={{ padding: '16px 12px 4px', fontSize: 11, fontWeight: 500, color: 'var(--t-3)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
              Management
            </div>
            {managementItems.map((item) => (
              <NavItem
                key={item.view}
                item={item}
                active={view === item.view}
                onClick={() => setView(item.view)}
                badge={item.view === 'incidents' && openCount > 0 ? openCount : undefined}
              />
            ))}
          </>
        )}
      </div>

      <div style={{ borderTop: '0.5px solid var(--bd-0)', padding: '10px 8px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', borderRadius: 'var(--radius)' }}>
          <div style={{
            width: 26, height: 26, borderRadius: '50%',
            background: 'var(--info-bg)', border: '0.5px solid var(--info-bd)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 500, color: 'var(--info)', flexShrink: 0,
          }}>
            {initials(user?.display_name, user?.email)}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--t-0)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.display_name ?? user?.email ?? 'Signed in'}
            </div>
            <div style={{ fontSize: 10, color: 'var(--t-2)' }}>{primaryRoleLabel(user?.permissions)}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void signOut()}
          style={{
            width: '100%',
            fontSize: 11,
            fontWeight: 500,
            color: 'var(--t-2)',
            textAlign: 'center',
            padding: '8px',
            borderRadius: 'var(--radius)',
            cursor: 'pointer',
            marginTop: 4,
            border: '0.5px solid var(--bd-1)',
            background: 'transparent',
            fontFamily: 'var(--font)',
          }}
        >
          Sign out
        </button>
      </div>
    </aside>
  )
}

function NavItem({
  item, active, onClick, badge,
}: {
  item: NavItemDef
  active: boolean
  onClick: () => void
  badge?: number
}) {
  return (
    <div
      onClick={onClick}
      className={active ? 'nav-item nav-item-active' : 'nav-item'}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 10px', margin: '1px 8px', borderRadius: 'var(--radius)',
        color: active ? 'var(--t-0)' : 'var(--t-1)',
        background: active ? 'var(--bg-3)' : 'transparent',
        fontWeight: active ? 500 : 400,
        cursor: 'pointer', fontSize: 13,
      }}
    >
      <span style={{ width: 13, height: 13, flexShrink: 0, opacity: active ? 0.85 : 0.45, display: 'flex' }}>
        {ICONS[item.icon]}
      </span>
      {item.label}
      {badge !== undefined && (
        <span style={{
          marginLeft: 'auto', fontSize: 10, padding: '1px 5px', borderRadius: 8,
          background: 'var(--crit-bg)', color: 'var(--crit)',
          border: '0.5px solid var(--crit-bd)', fontWeight: 500,
        }}>{badge}</span>
      )}
    </div>
  )
}

function highlightMatch(text: string, query: string): ReactNode {
  const q = query.trim()
  if (!q) return text
  const lower = text.toLowerCase()
  const qi = lower.indexOf(q.toLowerCase())
  if (qi < 0) return text
  return (
    <>
      {text.slice(0, qi)}
      <mark style={{ background: 'var(--info-bg)', color: 'var(--info)', padding: 0 }}>{text.slice(qi, qi + q.length)}</mark>
      {text.slice(qi + q.length)}
    </>
  )
}

function CatalogFilterSection({
  title, items, counts, activeCounts, selected, onSelect,
}: {
  title: string
  items: CatalogItem[]
  counts: Map<string, number>
  activeCounts: Map<string, number>
  selected: string | null
  onSelect: (code: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items
    return items.filter((it) =>
      it.name.toLowerCase().includes(q) || it.code.toLowerCase().includes(q),
    )
  }, [items, query])

  const selectedItem = selected ? items.find((it) => it.code === selected) ?? null : null
  const matchCount = query.trim() ? filtered.length : items.length

  return (
    <div style={{ marginTop: 8 }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 12px 4px', cursor: 'pointer',
          fontSize: 11, fontWeight: 500, color: 'var(--t-3)',
          textTransform: 'uppercase', letterSpacing: '.08em',
        }}
      >
        <span style={{
          display: 'flex', width: 9, height: 9, flexShrink: 0, opacity: 0.6,
          transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s ease',
        }}>
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 3l5 5-5 5" /></svg>
        </span>
        {title}
        <span style={{
          fontSize: 10, fontWeight: 500, letterSpacing: 0, textTransform: 'none',
          color: 'var(--t-2)',
        }}>
          ({matchCount})
        </span>
        {selectedItem ? (
          <span
            onClick={(e) => { e.stopPropagation(); onSelect(null) }}
            style={{
              marginLeft: 'auto', fontSize: 10, fontWeight: 500, letterSpacing: 0, textTransform: 'none',
              color: 'var(--info)', background: 'var(--info-bg)',
              border: '0.5px solid var(--info-bd)', borderRadius: 8, padding: '1px 6px', cursor: 'pointer',
            }}
          >
            {selectedItem.code} ×
          </span>
        ) : null}
      </div>

      {open && (
        <div style={{ padding: '2px 8px 4px' }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${title.toLowerCase()}…`}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '5px 8px', marginBottom: 4,
              fontSize: 12, color: 'var(--t-0)',
              background: 'var(--bg-2)', border: '0.5px solid var(--bd-0)',
              borderRadius: 'var(--radius)', outline: 'none',
            }}
          />
          <div style={{ maxHeight: 180, overflowY: 'auto' }}>
            {filtered.length === 0 && (
              <div style={{ padding: '8px', fontSize: 11, color: 'var(--t-2)' }}>No matches</div>
            )}
            {filtered.map((it) => {
              const selectedRow = it.code === selected
              const inventory = counts.get(it.code) ?? 0
              const activeN = activeCounts.get(it.code) ?? 0
              return (
                <div
                  key={it.code}
                  onClick={() => onSelect(selectedRow ? null : it.code)}
                  style={{
                    display: 'flex', alignItems: 'baseline', gap: 6,
                    padding: '6px 8px', margin: '1px 0', borderRadius: 'var(--radius)',
                    color: selectedRow ? 'var(--t-0)' : 'var(--t-1)',
                    background: selectedRow ? 'var(--bg-3)' : 'transparent',
                    cursor: 'pointer', fontSize: 12,
                  }}
                >
                  <span style={{ fontWeight: 600, fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
                    {highlightMatch(it.code, query)}
                    <span style={{ color: 'var(--t-2)', fontWeight: 400 }}>
                      {' '}{formatCatalogCount(activeN, inventory)}
                    </span>
                  </span>
                  <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--t-2)', textAlign: 'right', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {highlightMatch(it.name, query)}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
